import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Category, Product } from '@infrab4a/connect';
import { CatalogService } from '@infrab4a/connect-angular';
import { MenuItem, MessageService } from 'primeng/api';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-glam-new-home',
  templateUrl: './glam-new-home.component.html',
  styleUrl: './glam-new-home.component.scss'
})
export class GlamNewHomeComponent implements OnInit {
  private shopSettingsService = inject(ShopSettingsService);
  private messageService = inject(MessageService);
  private router = inject(Router);

  settings: any;
  sections: any[];
  productsByCategoryId: Record<string, Product[]> = {};
  loadingProductsByCategoryId: Record<string, boolean> = {};
  /**
   * Bloco estático "Economia glam" (B4A-13843): o admin só controla a posição
   * via drag-drop; o conteúdo é fixo no glam-frontend. Fora do enum do
   * `@infrab4a/connect`, que repassa tipos desconhecidos sem filtrar.
   */
  static readonly GLAM_SAVINGS_TYPE = 'GLAM_SAVINGS';

  menuItems: MenuItem[] = [
    { label: 'Adicionar Banner', routerLink: ['banner'] },
    { label: 'Adicionar Coleção', routerLink: ['collection'] },
    {
      label: 'Adicionar Economia glam',
      command: () => void this.addGlamSavingsSection()
    }
  ];
  form = new FormGroup({});

  constructor(readonly catalogService: CatalogService) {}

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById('GLAM_NEW_HOME');
    this.sections = this.settings.sections;
    void this.loadCollectionProducts();
  }

  onReorder(event: any[]) {
    console.log('Reorder event:', event);
  }

  async update() {
    LoaderService.showLoader();
    await this.shopSettingsService.update({
      id: this.settings.id,
      sections: this.sections
    });

    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Página reordenada com sucesso'
    });
  }

  removeSection(index: number) {
    this.sections.splice(index, 1);
    this.update();
  }

  hasGlamSavingsSection(): boolean {
    return (this.sections ?? []).some(
      (section) => section?.type === GlamNewHomeComponent.GLAM_SAVINGS_TYPE
    );
  }

  /**
   * Insere o bloco no fim da lista (a posição é ajustada arrastando). Só um
   * bloco por home: o segundo clique avisa e não persiste nada.
   */
  async addGlamSavingsSection(): Promise<void> {
    if (this.hasGlamSavingsSection()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'A home já tem o bloco Economia glam. Arraste para mudar a posição.'
      });
      return;
    }

    this.sections = [
      ...(this.sections ?? []),
      {
        id: crypto.randomUUID(),
        type: GlamNewHomeComponent.GLAM_SAVINGS_TYPE,
        fixed: false,
        editable: false
      }
    ];

    await this.update();
  }

  // Reordenação usando Angular CDK DragDrop
  async onDrop(event: CdkDragDrop<any[]>) {
    // Não permite mover se o item de origem ou destino for fixed
    const dragged = this.sections[event.previousIndex];
    const target = this.sections[event.currentIndex];
    if (dragged.fixed || target.fixed) {
      return;
    }
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);

    await this.update();
  }

  editSection(index: number) {
    const section = this.sections[index];

    switch (section.type) {
      case 'COLLECTION':
        this.router.navigate([
          `/configuracoes/glam/new-home/collection/${section.id}`
        ]);
        break;
      case 'LIVE':
        this.router.navigate([
          `/configuracoes/glam/new-home/live/${section.id}`
        ]);
        break;
      case 'BRANDS':
        this.router.navigate([
          `/configuracoes/glam/new-home/brands/${section.id}`
        ]);
        break;
      case 'CAROUSEL':
        this.router.navigate([
          `/configuracoes/glam/new-home/carousel/${section.id}`
        ]);
        break;
      case 'BANNER':
        this.router.navigate([
          `/configuracoes/glam/new-home/banner/${section.id}`
        ]);
        break;
      case 'INFOBARS':
        this.router.navigate([
          `/configuracoes/glam/new-home/infobar/${section.id}`
        ]);
        break;
      case 'HIGHLIGHTS':
        this.router.navigate([
          `/configuracoes/glam/new-home/highlights/${section.id}`
        ]);
        break;
    }
  }

  mapDisplayRuleText(ruleObj: any): string {
    const ruleMapping: { [key: string]: string } = {
      subscriberBuyer: 'Assinante já comprou',
      notSubscriber: 'Não assinante',
      subscriberNotBuyer: 'Assinante nunca comprou',
      topBadgeSubscriber: 'Assinante top badge',
      beautyProfile: 'Perfil de beleza'
    };

    for (const key in ruleObj) {
      const label = ruleMapping[key] || key;
      const value = ruleObj[key] ? 'Sim' : 'Não';

      return `${label}: ${value}`;
    }

    return '';
  }

  async getProducts(id: string): Promise<Product[]> {
    if (!id) {
      return [];
    }

    const products = await this.catalogService
      .fetchProducts({
        category: Category.toInstance({ id }),
        perPage: 8,
        page: 0,
        sort: 'most-relevant'
      })
      .then((res) => res.products.data || [])
      .catch(() => []);

    return products;
  }

  private async loadCollectionProducts(): Promise<void> {
    const categoryIds = this.sections
      ?.filter((section) => section.type === 'COLLECTION' && section.categoryId)
      .map((section) => section.categoryId);

    if (!categoryIds?.length) {
      return;
    }

    for (const categoryId of [...new Set(categoryIds)]) {
      if (
        this.loadingProductsByCategoryId[categoryId] ||
        this.productsByCategoryId[categoryId]
      ) {
        continue;
      }

      this.loadingProductsByCategoryId[categoryId] = true;

      try {
        this.productsByCategoryId[categoryId] =
          await this.getProducts(categoryId);
      } finally {
        this.loadingProductsByCategoryId[categoryId] = false;
      }
    }
  }
}
